import mapboxgl from 'mapbox-gl';
import bbox from '@turf/bbox';

import { isMobile } from '../utils/breakpoint';
import { createMap, renderSubways } from '../utils/map';
import { debounce } from '../utils/debounce';

import PulsingDot from '../utils/pulsing_dot';

// Cluster
const CLUSTER_MAX_ZOOM = 15;
const CLUSTER_RADIUS = 60;

// Markers
const FONT = ['Gotham Medium', 'DIN Offc Pro Medium', 'Arial Unicode MS Bold'];
const FONT_SIZE = 10;

const BASE_CIRCLE_RADIUS = 25;
const TEXT_COLOR = '#165671';

const BG_COLOR = '#fff';
const BG_COLOR_HOVER = '#ecfdff';

const BORDER_COLOR = '#165671';
const BORDER_WIDTH = 3;

const ListingsMapHook = {
  mounted() {
    const isVisible = this.el.checkVisibility();
    this.isInitialized = false;

    this.buildingsData = {
      type: 'FeatureCollection',
      features: [],
    };

    this.markers = {};
    this.markersOnScreen = {};

    this.clusterMarkers = {};
    this.clusterMarkersOnScreen = {};

    if (isVisible) {
      this.initMap();
    }

    this.handleEvent('listings:map-data', ({ data }) => {
      this.setData(data);
      this.isInitialized && this.fitBounds(data);
    });

    this.el.addEventListener('shown', () => {
      requestAnimationFrame(() => {
        this.initMap();
      });
    });
  },

  initMap() {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    this.map = createMap(this.el, -73.932803, 40.702891, {
      zoom: 11,
      scrollZoom: true,
    });

    this.map.loadImage('/images/expand.png', (error, image) => {
      if (error) throw error;
      // add image to the active style and make it SDF-enabled
      this.map.addImage('expand', image, { sdf: true, pixelRatio: 2 });
    });

    this.map.on('load', () => {
      this.map.addImage('pulsing-dot', PulsingDot, { pixelRatio: 2 });

      renderSubways(this.map);

      this.setupSource();

      this.setupMarkers();
      this.setupClusterMarkers();
      this.fitBounds(this.buildingsData);
    });

    this.map.on('moveend', debounce(this.handleBoundsChange.bind(this), 200));
  },

  fitBounds(data) {
    if (!data) return;
    if (data.features.length === 0) return;

    const bounds = bbox(data);
    this.map.fitBounds(bounds, { padding: 40, maxZoom: 14 });
  },

  setData(buildingsData) {
    const source = this.map && this.map.getSource('buildings');

    // Source initializes after map is loaded.
    if (source) {
      source.setData(buildingsData);
    } else {
      this.buildingsData = buildingsData;
    }
  },

  handleBoundsChange(e) {
    if (!e.originalEvent) return;

    const bounds = this.map.getBounds();

    const data = {
      north_east: bounds.getNorthEast(),
      south_west: bounds.getSouthWest(),
    };

    this.pushEvent('set-bounds', { bounds: data });
  },

  setupSource() {
    this.map.addSource('buildings', {
      type: 'geojson',
      cluster: true,
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
      clusterRadius: CLUSTER_RADIUS,
      data: this.buildingsData,
      clusterProperties: {
        total_listing_count: ['+', ['get', 'listing_count']],
        buildings: ['concat', ['id']],
      },
    });
  },

  setupClusterMarkers() {
    let hoveredClusterId = null;

    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'buildings',
      filter: ['has', 'point_count'],
      paint: {
        // * 20px circles when point count is less than 20
        // * 30px circles when point count is between 20 and 100
        // * 40px circles when point count is greater than or equal to 100
        'circle-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          BG_COLOR_HOVER,
          BG_COLOR,
        ],
        'circle-radius': [
          'step',
          ['get', 'total_listing_count'],
          BASE_CIRCLE_RADIUS,
          30,
          BASE_CIRCLE_RADIUS * 1.5,
          100,
          BASE_CIRCLE_RADIUS * 2,
        ],
        // 'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
        'circle-stroke-color': BORDER_COLOR,
        'circle-stroke-width': BORDER_WIDTH,
      },
    });

    this.map.addLayer({
      id: 'clusters-label',
      type: 'symbol',
      source: 'buildings',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'total_listing_count'],
        'text-font': FONT,
        'text-size': FONT_SIZE,
        // 'text-allow-overlap': true,
        'icon-image': 'expand',
      },
      paint: {
        'text-color': TEXT_COLOR,
        'icon-color': TEXT_COLOR,
        'text-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0, 1],
        'icon-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0],
      },
    });

    this.map.on('mousemove', 'clusters', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
      });
      const clusterId = features[0].properties.cluster_id;

      if (hoveredClusterId !== null) {
        this.map.setFeatureState({ source: 'buildings', id: hoveredClusterId }, { hover: false });
      }

      hoveredClusterId = clusterId;
      this.map.setFeatureState({ source: 'buildings', id: hoveredClusterId }, { hover: true });
    });

    this.map.on('mouseleave', 'clusters', () => {
      if (hoveredClusterId !== null) {
        this.map.setFeatureState({ source: 'buildings', id: hoveredClusterId }, { hover: false });
      }
      hoveredClusterId = null;
    });

    // Expand cluster on click
    this.map.on('click', 'clusters', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
      });

      const clusterId = features[0].properties.cluster_id;

      this.map.getSource('buildings').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;

        this.map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom,
          duration: 150,
        });
      });
    });
  },

  setupMarkers() {
    let hoveredBuildingId = null;

    this.map.addLayer({
      id: 'buildings',
      type: 'circle',
      source: 'buildings',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          BG_COLOR_HOVER,
          BG_COLOR,
        ],
        'circle-radius': BASE_CIRCLE_RADIUS,

        'circle-stroke-color': BORDER_COLOR,
        'circle-stroke-width': BORDER_WIDTH,
      },
    });

    this.map.addLayer({
      id: 'buildings-label',
      type: 'symbol',
      source: 'buildings',
      filter: ['!', ['has', 'point_count']],
      layout: {
        // If there is more than 1 listing then show the count.
        // Otherwise show the label (price).
        'text-field': [
          'case',
          ['==', ['get', 'listing_count'], 1],
          ['get', 'label'],
          ['get', 'listing_count'],
        ],
        'text-font': FONT,
        'text-size': FONT_SIZE,
        // 'text-allow-overlap': true,
      },
      paint: {
        'text-color': TEXT_COLOR,

        // Hide text when loading indicator shown
        'text-opacity': ['case', ['boolean', ['feature-state', 'loading'], false], 0, 1],
      },
    });

    this.map.on('mousemove', 'buildings', (e) => {
      if (e.features.length > 0) {
        if (hoveredBuildingId !== null) {
          this.map.setFeatureState(
            { source: 'buildings', id: hoveredBuildingId },
            { hover: false },
          );
        }

        hoveredBuildingId = e.features[0].id;
        this.map.setFeatureState({ source: 'buildings', id: hoveredBuildingId }, { hover: true });
      }
    });

    this.map.on('mouseleave', 'buildings', () => {
      if (hoveredBuildingId !== null) {
        this.map.setFeatureState({ source: 'buildings', id: hoveredBuildingId }, { hover: false });
      }
      hoveredBuildingId = null;
    });

    this.map.on('click', 'buildings', (e) => {
      const feature = e.features[0];
      const props = feature.properties;
      const coords = feature.geometry.coordinates.slice();

      // Show loading indicator
      // We can't use feature states with symbol layout or with a layer filter (both are not supported).
      // The one alternative is to use opacity (set text opacity to 0 and icon opacity to 1 when loading)
      //   but we don't want to keep bunch of animated icons alive (even hidden).
      this.map.setLayoutProperty('buildings-label', 'icon-image', [
        'case',
        ['==', ['id'], feature.id],
        'pulsing-dot',
        '',
      ]);
      this.map.setFeatureState({ source: 'buildings', id: feature.id }, { loading: true });

      this.pushEvent('expand-building', { id: feature.id }, ({ body }, ref) => {
        // Hide loading indicator
        this.map.setLayoutProperty('buildings-label', 'icon-image', '');
        this.map.setFeatureState({ source: 'buildings', id: feature.id }, { loading: false });

        new mapboxgl.Popup({
          closeButton: false,
          maxWidth: 'none',
          offset: 30,
          className:
            props.listing_count == 1 ? 'listing-map-listing-popup' : 'listing-map-building-popup',
          closeOnMove: true,
        })
          .setLngLat(coords)
          .setHTML(body)
          .addTo(this.map);
      });
    });
  },
};

export default ListingsMapHook;
